import {DocumentType, documentTypeToJSON} from "@/pb/api/devplan/types/documents";

export function companyPath<T extends number | string>(companyId: T) {
  return `/company/${companyId}` as const;
}

export function homeReportPath<T extends number | string>(companyId: T, reportId: string) {
  return `${companyPath(companyId)}/digests/${reportId}` as const;
}

export function projectsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/projects` as const;
}

export function projectsListPath<T extends number | string>(companyId: T) {
  return `${projectsPath(companyId)}/list` as const;
}

export function projectsRoadmapPath<T extends number | string>(companyId: T) {
  return `${projectsPath(companyId)}/roadmap` as const;
}

export function projectsKanbanPath<T extends number | string>(companyId: T) {
  return `${projectsPath(companyId)}/kanban` as const;
}

export function roadmapPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/roadmap` as const;
}

export function integrationsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/integrations` as const;
}

export function memoriesPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/memories` as const;
}

export function decisionsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/decisions` as const;
}

export function goalsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/goals` as const;
}

export function goalPath<T extends number | string, G extends string>(companyId: T, goalId: G) {
  return `${goalsPath(companyId)}/${goalId}` as const;
}

export function dashboardsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/dashboards` as const;
}

export function dashboardPath<T extends number | string, D extends string>(companyId: T, dashboardId: D) {
  return `${dashboardsPath(companyId)}/${dashboardId}` as const;
}

export function integrationsUploadsPath<T extends number | string>(companyId: T) {
  return `${integrationsPath(companyId)}/uploads` as const;
}

export function integrationsNotionPath<T extends number | string>(companyId: T) {
  return `${integrationsPath(companyId)}/notion` as const;
}

export function integrationsConfluencePath<T extends number | string>(companyId: T) {
  return `${integrationsPath(companyId)}/confluence` as const;
}

export function integrationsGoogleDrivePath<T extends number | string>(companyId: T) {
  return `${integrationsPath(companyId)}/google-drive` as const;
}

export function integrationsAtlassianSitePath<T extends number | string>(
  companyId: T,
  provider: "jira" | "confluence",
  returnPath?: string,
) {
  const base = `${integrationsPath(companyId)}/atlassian-site` as const;
  const params = new URLSearchParams({provider});
  if (returnPath) params.set("returnPath", returnPath);
  return `${base}?${params.toString()}` as const;
}

export function quickWinsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/features` as const;
}

export function bugsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/bugs` as const;
}

export function projectPath<T extends number | string, P extends string>(companyId: T, projectId: P) {
  return `${projectsPath(companyId)}/${projectId}` as const;
}

export function tabLayoutTabPath<T extends number | string, P extends string, B extends string>(companyId: T, projectId: P, tab: B) {
  return `${projectPath(companyId, projectId)}/${tab}` as const;
}

export function productRequirementsTabPath<T extends number | string, P extends string>(companyId: T, projectId: P) {
  return `${tabLayoutTabPath(companyId, projectId, "product")}?view=requirements` as const;
}

export function quickWinPath<T extends number | string, P extends string>(companyId: T, projectId: P) {
  return tabLayoutTabPath(companyId, projectId, "feature");
}

export function bugPath<T extends number | string, P extends string>(companyId: T, projectId: P) {
  return tabLayoutTabPath(companyId, projectId, "bug");
}

export function tabLayoutCustomTabPath<T extends number | string, P extends string, D extends string>(companyId: T, projectId: P, documentId: D) {
  return `${projectPath(companyId, projectId)}/custom/${documentId}` as const;
}

export function companyCreatePath() {
  return `/company/onboard` as const;
}

export function prelaunchPath() {
  return `/prelaunch` as const;
}

export function signInPath(recaptchaRequired = false) {
  return recaptchaRequired ? `/?recaptcha=required` as const : `/` as const;
}

export function companyOnboardingPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/onboard` as const;
}

export function companyOnboardingBillingPath<T extends number | string>(
  companyId: T,
  checkout?: "success" | "cancel",
) {
  const path = `${companyOnboardingPath(companyId)}?step=billing` as const;
  return checkout ? `${path}&checkout=${checkout}` as const : path;
}

export function settingsPath() {
  return `/settings` as const;
}

export function settingsProfilePath() {
  return `${settingsPath()}/profile` as const;
}

export function settingsProfilePersonalPath() {
  return `${settingsProfilePath()}/personal` as const;
}

export function settingsProfilePreferencesPath() {
  return `${settingsProfilePath()}/preferences` as const;
}

export function settingsProfileHomeReportPreferencesPath<T extends number | string>(companyId: T) {
  return `${settingsProfilePreferencesPath()}?homeReportWorkspaceId=${encodeURIComponent(String(companyId))}` as const;
}

export function settingsProfileBillingPath() {
  return `${settingsProfilePath()}/billing` as const;
}

export function settingsProfileUsagePath() {
  return `${settingsProfilePath()}/usage` as const;
}

export function settingsProfileWorkspacesPath() {
  return `${settingsProfilePath()}/workspaces` as const;
}

export function settingsWorkspacePath<T extends number | string>(companyId: T) {
  return `${settingsPath()}/workspace/${companyId}` as const;
}

export function settingsWorkspaceMembersPath<T extends number | string>(companyId: T) {
  return `${settingsWorkspacePath(companyId)}/members` as const;
}

export function settingsWorkspaceIntegrationsPath<T extends number | string>(companyId: T) {
  return `${settingsWorkspacePath(companyId)}/integrations` as const;
}

export type IntegrationsV2GitProvider = "github" | "bitbucket" | "gitlab";

export function integrationsGitProviderPath<T extends number | string>(
  companyId: T,
  provider: IntegrationsV2GitProvider,
) {
  return `${integrationsPath(companyId)}/${provider}` as const;
}

export type IntegrationsV2TicketProvider = "jira" | "linear";

export function integrationsTicketProviderPath<T extends number | string>(
  companyId: T,
  provider: IntegrationsV2TicketProvider,
) {
  return `${integrationsPath(companyId)}/${provider}` as const;
}

export function integrationsGranolaPath<T extends number | string>(
  companyId: T,
) {
  return `${integrationsPath(companyId)}/granola` as const;
}

export function integrationsSlackPath<T extends number | string>(
  companyId: T,
) {
  return `${integrationsPath(companyId)}/slack` as const;
}

export function slackLandingPath() {
  return "/slack" as const;
}

export function slackInstallPath() {
  return `${slackLandingPath()}/install` as const;
}

export function slackInstallSignInPath() {
  return `/?callbackUrl=${encodeURIComponent(slackInstallPath())}` as const;
}

export function integrationsManagedConnectionToolsPath<T extends number | string, S extends string>(
  companyId: T,
  slug: S,
) {
  return `${integrationsPath(companyId)}/managed-connections/${encodeURIComponent(slug)}` as const;
}

export function integrationsTeamsPath<T extends number | string>(
  companyId: T,
) {
  return `${integrationsPath(companyId)}/teams` as const;
}

export function integrationsZoomPath<T extends number | string>(
  companyId: T,
) {
  return `${integrationsPath(companyId)}/zoom` as const;
}

export const integrationTabs = ["code", "tickets", "meeting-notes"] as const;
export type IntegrationTab = typeof integrationTabs[number];

export function settingsWorkspaceIntegrationTabPath<T extends number | string>(companyId: T, tab: IntegrationTab) {
  return `${settingsWorkspaceIntegrationsPath(companyId)}/${tab}` as const;
}

export function settingsWorkspaceGeneralPath<T extends number | string>(companyId: T) {
  return `${settingsWorkspacePath(companyId)}/general` as const;
}

export function settingsWorkspaceApiKeysPath<T extends number | string>(companyId: T) {
  return `${settingsWorkspacePath(companyId)}/api-keys` as const;
}

export function settingsWorkspaceDevelopmentPath<T extends number | string>(companyId: T) {
  return `${settingsWorkspacePath(companyId)}/development` as const;
}

export function settingsWorkspaceSetupRunsPath<T extends number | string>(companyId: T) {
  return `${settingsWorkspacePath(companyId)}/setup-runs` as const;
}

export function settingsWorkspaceReportingPath<T extends number | string>(companyId: T) {
  return `${settingsWorkspacePath(companyId)}/reporting` as const;
}

export function settingsWorkspaceTeamsPath<T extends number | string>(companyId: T) {
  return `${settingsWorkspacePath(companyId)}/teams` as const;
}

export function settingsWorkspaceTeamPath<T extends number | string>(companyId: T, teamId: string) {
  return `${settingsWorkspaceTeamsPath(companyId)}/${teamId}` as const;
}

export function settingsOrganizationPath<T extends string>(orgId: T) {
  return `${settingsPath()}/organization/${orgId}` as const;
}

export function settingsOrganizationSettingsPath<T extends string>(orgId: T) {
  return `${settingsOrganizationPath(orgId)}/general` as const;
}

export function settingsOrganizationBillingPath<T extends string>(orgId: T) {
  return `${settingsOrganizationPath(orgId)}/billing` as const;
}

export function settingsOrganizationBillingUsagePath<T extends string>(orgId: T) {
  return `${settingsOrganizationBillingPath(orgId)}/usage` as const;
}

export function settingsOrganizationWorkspacesPath<T extends string>(orgId: T) {
  return `${settingsOrganizationPath(orgId)}/workspaces` as const;
}

export function companySettingsPath<T extends number | string>(companyId: T) {
  return settingsWorkspaceMembersPath(companyId);
}

export function companySettingsTabPath<T extends number | string, S extends string>(companyId: T, tab: S) {
  return `${settingsWorkspacePath(companyId)}/${tab}` as const;
}

export function companySetupRunsPath<T extends number | string>(companyId: T) {
  return settingsWorkspaceSetupRunsPath(companyId);
}

export function documentsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/docs` as const;
}

export function documentPath<T extends string, C extends number | string>(companyId: C, documentId: T) {
  return `${documentsPath(companyId)}/${documentId}` as const;
}

export function adminTemplatesPath() {
  return `/admin/templates` as const
}

export function projectTemplatePath<T extends string>(templateId: T) {
  return `/admin/templates/project/editor/edit/${templateId}` as const
}

export function documentTemplatePath<T extends string>(templateId: T) {
  return `/admin/templates/editor/edit/${templateId}` as const
}

export function documentTemplateCreatePath<T extends DocumentType>(docType: T) {
  return `/admin/templates/editor/new?docType=${documentTypeToJSON(docType)}` as const
}

export function adminTiersPath() {
  return `/admin/tiers` as const
}

export function adminTierEditPath<T extends number | string>(tierId: T) {
  return `${adminTiersPath()}/edit/${tierId}` as const
}

export function adminTierCreatePath() {
  return `${adminTiersPath()}/new` as const
}

export function adminOrganizationsPath() {
  return `/admin/organizations` as const
}

export function adminOnboardingPath() {
  return `/admin/onboarding` as const
}

export function adminOrganizationPath<T extends string>(organizationId: T) {
  return `${adminOrganizationsPath()}/${encodeURIComponent(organizationId)}` as const
}

export function adminCompanyIdSearchPath() {
  return `/admin/company-id-search` as const
}

export function adminMarkdownDirectivePlaygroundPath() {
  return `/admin/markdown-directive-playground` as const
}

export function adminDashboardPlaygroundPath() {
  return `/admin/dashboard-playground` as const
}

export function adminLlmUsagePath() {
  return `/admin/llm-usage` as const
}

export function adminCompanyPropsPath<T extends number | string>(companyId: T) {
  return `/admin/companies/${companyId}/props` as const
}

export function ideasPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/ideas` as const;
}

export function profilePath() {
  return settingsProfilePersonalPath();
}

export function profilePreferencesPath() {
  return settingsProfilePreferencesPath();
}

export function profilePersonalPath() {
  return settingsProfilePersonalPath();
}

export function profileBillingPath() {
  return settingsProfileBillingPath();
}

export function profileUsagePath() {
  return settingsProfileUsagePath();
}

export function profileWorkspacesPath() {
  return settingsProfileWorkspacesPath();
}

export function knowledgePath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/knowledge` as const;
}

export function knowledgeStatePath<T extends number | string>(
  companyId: T,
  params?: Record<string, string | undefined | null>,
) {
  const base = knowledgePath(companyId)
  if (!params) return base

  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      searchParams.set(key, value)
    }
  }

  const query = searchParams.toString()
  return query ? `${base}?${query}` as const : base
}

export function templatesBuilderPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/templates/builder` as const;
}

export function knowledgeCompanyPath<T extends number | string>(companyId: T) {
  return `${knowledgePath(companyId)}/company` as const;
}

export function knowledgeReposPath<T extends number | string>(companyId: T) {
  return `${knowledgePath(companyId)}/repositories` as const;
}

export function repoResearchPath<T extends number | string, R extends string>(companyId: T, repoId: R) {
  return `${knowledgeReposPath(companyId)}/research/${repoId}` as const;
}

export function repoResearchDirPath<T extends number | string, R extends string, D extends string>(
  companyId: T, repoId: R, dirKey: D,
) {
  return `${repoResearchPath(companyId, repoId)}/area/${dirKey}` as const;
}

export function knowledgeWebsitePath<T extends number | string>(companyId: T) {
  return `${knowledgePath(companyId)}/website` as const;
}

export function knowledgeUserFlowsPath<T extends number | string>(companyId: T) {
  return `${knowledgePath(companyId)}/user-flows` as const;
}

export function knowledgeFeatureCataloguePath<T extends number | string>(companyId: T) {
  return `${knowledgePath(companyId)}/feature-catalog` as const;
}

export function reposCompanyRedirectPath() {
  return `/redirect/company/knowledge/repositories` as const;
}

export function changelogPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/changelog` as const;
}

export function signalsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/signals` as const;
}

export function insightsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/insights` as const;
}

export function insightPath<T extends number | string, I extends string>(companyId: T, insightId: I) {
  return `${insightsPath(companyId)}/${encodeURIComponent(insightId)}` as const;
}

export function radarPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/radar` as const;
}

export function radarInsightsPath<T extends number | string>(companyId: T) {
  return `${radarPath(companyId)}/insights` as const;
}

export function radarInsightPath<T extends number | string, I extends string>(companyId: T, insightId: I) {
  return `${radarPath(companyId)}/${encodeURIComponent(insightId)}` as const;
}

export function companyEvidenceBundlePath<T extends number | string, B extends string>(companyId: T, bundleId: B) {
  return `${companyPath(companyId)}/evidence/${bundleId}` as const;
}

export function trendsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/trends` as const;
}

export function historyPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/history` as const;
}

export function changesReportsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/changes-reports` as const;
}

export function changesReportPath<T extends number | string, R extends string>(companyId: T, reportId: R) {
  return `${changelogPath(companyId)}/i/${reportId}` as const;
}

export function orgsPath() {
  return `/org` as const;
}

export function orgPath<T extends string>(orgId: T) {
  return `${orgsPath()}/${orgId}` as const;
}

export function orgSettingsPath<T extends string>(orgId: T) {
  return settingsOrganizationSettingsPath(orgId);
}

export function orgBillingPath<T extends string>(orgId: T) {
  return settingsOrganizationBillingPath(orgId);
}

export function orgUsagePath<T extends string>(orgId: T) {
  return settingsOrganizationBillingUsagePath(orgId);
}

export function orgNewPath() {
  return `${orgsPath()}/new` as const;
}

export function apiOrgInvoicePreviewPath<T extends string>(orgId: T) {
  return `/api/v1/org/${orgId}/billing/invoice` as const;
}

export function jobsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/jobs` as const;
}

export function jobPath<T extends number | string, J extends string>(companyId: T, jobId: J) {
  return `${jobsPath(companyId)}/${jobId}` as const;
}

export function runPath<T extends number | string, R extends string>(companyId: T, runId: R) {
  return `${jobsPath(companyId)}/runs/${runId}` as const;
}

export function taskRunsPath<T extends number | string, K extends string>(companyId: T, taskId: K) {
  return `${companyPath(companyId)}/tasks/${taskId}/runs` as const;
}

export function runsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/runs` as const;
}

export function chatsPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/chats` as const;
}

export function chatPath<T extends number | string, C extends string>(companyId: T, chatId: C) {
  return `${chatsPath(companyId)}/${chatId}` as const;
}

export function knowledgeSearchPath<T extends number | string>(companyId: T) {
  return `${companyPath(companyId)}/knowledge-search` as const;
}
